import { Box, Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Paper, PaperBorderType, SelectField, TextField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { delinquencyService } from '../delinquencyService';
import { useStore } from '@roc/client-portal-shared/hooks';

const sectionBlue = '#337ab7';

const useStyles = makeStyles((theme) => ({
  sectionPaper: {
    border: `1px solid ${sectionBlue} !important`,
    overflow: 'hidden',
  },
  sectionHeader: {
    backgroundColor: sectionBlue,
    color: '#fff',
    padding: theme.spacing(1.5, 2),
  },
}));

export type ForeclosureAttorneyFormData = {
  firmId: number | null;
  alias: string;
  phoneNumber: string;
  emailAddress: string;
  state: string;
};

export type ForeclosureAttorneyFormProps = {
  handleClose: () => void;
  properties?: any;
  loanId?: string;
  showAlert?: (message: string, type: 'success' | 'danger') => void;
  setIsLoading?: (key: string, value: boolean) => void;
  reloadLoan?: (rerenderGrid?: () => void) => void;
  open?: boolean;
};

function getFirmOptions(data: any[]) {
  return data.map((item) => ({
    label: item.company?.name ?? '',
    value: item.investorAttorneyId,
  }));
}

function findAttorneyById(attorneys: any[] | undefined, id: number) {
  return attorneys?.find(
    item => Number(item?.investorAttorneyId) === Number(id)
  );
}

export const ForeclosureAttorneyForm = observer(
  ({
    handleClose,
    loanId,
    showAlert,
    setIsLoading,
    reloadLoan,
    open
  }: ForeclosureAttorneyFormProps) => {
    const classes = useStyles();
    const { lenderDetailsStore } = useStore();
    const firmList = useMemo(
      () => getFirmOptions(lenderDetailsStore.allForeclosureAttorneys ?? []),
      [lenderDetailsStore.allForeclosureAttorneys]
    );
    const [investorAttorneyId, setFirmId] = useState<number | null>(null);
    const [firmValue, setFirmValue] = useState<string>('');
    const [alias, setAlias] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [emailAddress, setEmailAddress] = useState('');
    const [state, setState] = useState('');

    useEffect(() => {
      if (open) {
        const fetchAttorney = async () => {
          await lenderDetailsStore.fetchAllForeclosureAttorneys();
          await lenderDetailsStore.getForeclosureAttorneyByLoanId(loanId);
          if (lenderDetailsStore.savedForeclosureAttorney) {
            handleFirmChange(lenderDetailsStore?.savedForeclosureAttorney?.investorAttorneyId);
          }
        };
        fetchAttorney();
      }
    }, [lenderDetailsStore, open]);

    const handleFirmChange = useCallback(
      (value: number) => {
        const firm = findAttorneyById(lenderDetailsStore.allForeclosureAttorneys, value);
        setFirmId(value);
        setFirmValue(String(value));

        const c = firm?.company;
        setAlias(firm?.alias ?? c?.alias ?? c?.name ?? '');
        setPhoneNumber(firm?.phoneNumber ?? c?.phoneNumber ?? c?.phone ?? '');
        setEmailAddress(firm?.emailAddress ?? c?.emailAddress ?? c?.email ?? '');
        setState(firm?.state ?? c?.state ?? '');
      },
      [lenderDetailsStore]
    );

    const submitForeclosureAttorney = async () => {
      setIsLoading?.('isLoading', true);
      try {
        if (!investorAttorneyId) {
          showAlert?.(
            'Foreclosure attorney missing, please select a firm from the list',
            'danger'
          );
          return;
        }

        const request = {
          foreclosureAttorneyId: lenderDetailsStore.savedForeclosureAttorney ? lenderDetailsStore.savedForeclosureAttorney.foreclosureAttorneyId : investorAttorneyId,
          investorAttorneyId: investorAttorneyId,
        };

        await delinquencyService.updateForeclosureAttorneyFormV2(loanId, request);
        showAlert?.('Foreclosure attorney was successfully updated', 'success');
        handleClose();
        reloadLoan?.();
      } catch {
        showAlert?.(
          'Error while fetching the foreclosure attorney information',
          'danger'
        );
      } finally {
        setIsLoading?.('isLoading', false);
      }
    };


    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper
            borderType={PaperBorderType.SOLID_BORDER}
            className={classes.sectionPaper}
            titleComponent={
              <Box className={classes.sectionHeader}>
                <Typography variant="subtitle1">Attorney's Information</Typography>
              </Box>
            }
          >
            <Box px={2} pb={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <SelectField
                    label="Firm"
                    value={firmValue}
                    options={firmList}
                    onChange={handleFirmChange}
                    disabled={firmList.length === 0}
                    testId="foreclosure-attorney-firm"
                    fullWidth
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    label="Alias"
                    value={alias}
                    fullWidth
                    margin="normal"
                    testId="foreclosure-attorney-alias"
                    disabled
                  />
                  <TextField
                    variant="outlined"
                    label="Email Address"
                    value={emailAddress}
                    fullWidth
                    margin="normal"
                    testId="foreclosure-attorney-email"
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    variant="outlined"
                    label="Phone Number"
                    value={phoneNumber}
                    fullWidth
                    margin="normal"
                    testId="foreclosure-attorney-phone"
                    disabled
                  />
                  <TextField
                    variant="outlined"
                    label="State"
                    value={state}
                    fullWidth
                    margin="normal"
                    testId="foreclosure-attorney-state"
                    disabled
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
        <Grid container spacing={2} justifyContent="center">
          <Grid item>
            <Button
              type="button"
              variant="contained"
              color="default"
              testId="foreclosure-attorney-close"
              onClick={handleClose}
            >
              Close
            </Button>
          </Grid>
          <Grid item>
            <Button
              type="button"
              variant="contained"
              color="primary"
              testId="foreclosure-attorney-save"
              onClick={submitForeclosureAttorney}
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);
